/* eslint-disable */
// @ts-nocheck
// import { InteractionType } from "@azure/msal-browser";
import { useMsal } from '@azure/msal-react';
import { useQuery } from "@tanstack/react-query";
import { Flex, Tabs } from "antd";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
    getDestinationMetadata,
    getSourceEntities,
    getSourceProperties,
    getDefaultImportConfiguration,
    getPreview,
    saveImportConfiguration
} from "../../api/template/template";
import { useUserData } from "../../hooks/useUserProfile";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import { createErrorMessage } from "../../utils/createErrorMessage";
import { ErrorView } from "../Error/ErrorView";
import { FieldsList } from "../ImportConfigurationFieldMapping/FieldsList";
import { setDestinationIdentifier, selectImportConfiguration } from '../../store/slices/dataverseSlice';
import { CustomButton } from '../FormFields/CustomButton';
import { getClosestString } from '../../utils/getClosestString';
import { sortBy } from 'lodash-es';


interface SourceEntitySchema {
    identifier: string;
    displayName: string;
    properties: Array<{
        identifier: string;
        displayName: string;
    }>;
}

export const SourcesDataProvider = () => {
    const { instance } = useMsal();
    const account = instance.getActiveAccount();
    const { organizationId, tenantId } = useUserData();
    const dispatch = useAppDispatch();
    const { destinationIdentifier, selectedEntity, selectedInstance, operationIdentifier} = useAppSelector(state => state.dataverse);

     const { data: destinationMetadataData, isLoading: isLoadingDestinationMetadata } = useQuery({
        queryKey: ["getDestinationMetadata"],
        queryFn: async () => {
        try {
            const res = await getDestinationMetadata();
            return res.data.data;
        } catch (error) {
            toast.error(createErrorMessage(error));
        }
        },
        enabled: !!tenantId
    });

    const { data: sourcePropertiesData, error, isLoading, isError } = useQuery({
        queryKey: ['sourceProperties', selectedInstance, selectedEntity, organizationId, tenantId, operationIdentifier, account?.username],
        queryFn: async () => {
              try {
                const instanceToken = await instance.acquireTokenSilent({
                    account: account ?? undefined,
                    scopes: [`${selectedInstance}/.default`],
                });

                if (!instanceToken?.accessToken) {
                    throw new Error("No access token");
                }

                const res = await getSourceProperties(
                    organizationId,
                    tenantId,
                    "DataverseAdapter",
                    selectedEntity,
                    {
                        InstanceUrl: selectedInstance
                    },
                    operationIdentifier,
                    { "X-MS-Access-Token": instanceToken.accessToken }
                );
                
                return res.data.data;
              } catch (error) {
                toast.error(createErrorMessage(error));
              }
            },
        enabled: !!selectedInstance && !!selectedEntity && !!account // Only run the query if we have an access token
    });

    if (isLoading) return <p>Loading...</p>;

    if (isError) return <ErrorView error={error} showDetails />;

      const destinationItems = destinationMetadataData?.map(
        (property: SourceEntitySchema) => ({
            key: property.identifier,
            label: property.displayName || property.identifier,
            children: <ConfigureMappings
                sourceProperties={sourcePropertiesData}
                destinationProperties={destinationMetadataData.find((destination) => destination.identifier === destinationIdentifier) || {}}
            />
        })
    );
    
    return (
        <Flex vertical gap={16} align="center" justify="center">
            <ConfigureMappings
                sourceProperties={sourcePropertiesData}
                destinationProperties={destinationMetadataData?.find((destination) => destination.identifier === destinationIdentifier) || {}}
            />
            {/* <Tabs
                tabPosition="left"
                items={destinationItems}
                activeKey={destinationIdentifier}
                onChange={(key) => {
                    dispatch(setDestinationIdentifier(key));
                }}
            /> */}
        </Flex>
    )
}

const ConfigureMappings = ({ sourceProperties = [], destinationProperties = [] }) => {
    const { instance } = useMsal();
    const dispatch = useAppDispatch();
    const account = instance.getActiveAccount();
    // const [importConfiguration, setImportConfiguration] = useState(null);
    const { tenantId, organizationId } = useUserData();
    const {
        destinationIdentifier,
        selectedEntity,
        selectedInstance,
        operationIdentifier,
        selectedImportConfiguration,
        importConfigurations
    } = useAppSelector(state => state.dataverse);

    useEffect(() => {
        if (importConfigurations.length > 0) {
            const sorted = sortBy(importConfigurations, 'id');
            setImportConfiguration(sorted[0]);
        }
    }, []);

    // The default import configuration have sourceIdentifier same as destinationIdentifier
    // Here we merge current entity properties (source*) with default import configuration destination properties (destination*)
    // Should be done on the backend
    const adjustFieldMappingsForDefaultConfiguration = (defaultConfig, sourceProperties) => {
        // Get the field mappings from default configuration
        const defaultMappings = defaultConfig.importConfigurationFieldMappings;

        // Map the properties of selected source with matched the destination identifiers in default configuration
        const mergedFieldMappings = defaultMappings?.map((
                mapping: ImportConfigurationFieldMapping
        ) => {
            const sourceProperty = sourceProperties.find(
                (property) => (property.identifier === mapping.destinationFieldIdentifier || property.displayName === mapping.destinationFieldIdentifier)
            );

            const potentialSourceNames = sourceProperties.map((property) => property.displayName || property.identifier);

            const closestString = getClosestString(mapping.destinationFieldIdentifier, potentialSourceNames);

            const closestSourceProperty = sourceProperties.find(
                (property) => property.displayName === closestString || property.identifier === closestString
            );

            return {
                ...mapping,
                sourceFieldIdentifier: closestSourceProperty?.identifier,
                sourceDisplayName: closestSourceProperty?.displayName || closestSourceProperty?.identifier
            };
        }) || [];
    
        return {
            ...defaultConfig,
            sourceIdentifier: selectedEntity,
            importConfigurationFieldMappings: mergedFieldMappings
        };
    };

    const { data: defaultImportConfiguration, isLoading } = useQuery({
        queryKey: ["dataverse", "defaultImportConfiguration", destinationProperties.identifier],
        queryFn: async () => {
            const res = await getDefaultImportConfiguration(
                organizationId,
                tenantId,
                "DataverseAdapter",
                destinationIdentifier
            );

            const adjustedConfig = adjustFieldMappingsForDefaultConfiguration(res.data.data, sourceProperties);
            dispatch(setImportConfiguration(adjustedConfig));

            return adjustedConfig;
        },
        enabled: !!destinationIdentifier && !!tenantId && !!organizationId && !!selectImportConfiguration?.id,
    });

    const sourcePropertyOptions = sourceProperties.map((property) => ({
        label: property.displayName || property.identifier,
        value: property.identifier,
    }));

    const onSaveConfiguration = async () => {
        try {
            const config = { ...importConfiguration };
            const instanceUrlArgument = config.importConfigurationArguments?.find((item) => item.identifier === "InstanceUrl");
            if (instanceUrlArgument) {
                instanceUrlArgument.value = selectedInstance;
            } else {
                config.importConfigurationArguments.push({
                    identifier: "InstanceUrl",
                    value: selectedInstance
                });
            }
            // config.importConfigurationFieldMappings = importConfigurationFieldMappings;
            const res = await saveImportConfiguration(organizationId, config);

            
            dispatch(setImportConfiguration(res.data));
            // dispatch(setSliceSelectedImportConfiguration(res.data.data));
            // const tempMappings = res.data.data.importConfigurationFieldMappings
            //     .map((item: ImportConfigurationFieldMapping) => {
            //     return { ...item };
            //     });
            // setImportConfigurationFieldMappings(tempMappings);
        
            // if (!initialImportConfigurations) {
            //     throw new Error('initialImportConfigurations is null');
            // }
            // const updatedImportConfigurations = initialImportConfigurations.map(obj =>
            //     obj.id === config.id ? res.data.data : obj
            // );
            // dispatch(setSliceImportConfigurations(updatedImportConfigurations));
        } catch (error: any) {
            toast.error("Ooops! Something went wrong while saving the configuration. Please try again.");
        }
    };

    // TODO: expose this function to helpers
    // Same used in importConfigurationFieldMappings.tsx:200
    const handleGetPreview = async (config) => {
        const destinationSourceFieldMappings = config.importConfigurationFieldMappings
            .reduce<Record<string, string>>((dict, item) => {
                if (!item.sourceFieldIdentifier) {
                throw new Error('sourceFieldIdentifier is null');
                }
                dict[item.destinationFieldIdentifier] = item.sourceFieldIdentifier;
                return dict;
        }, {});

        const instanceToken = await instance.acquireTokenSilent({
            account: account ?? undefined,
            scopes: [`${selectedInstance}/.default`],
        });

        if (!instanceToken?.accessToken) {
            throw new Error("No access token");
        }

        const res = await getPreview(
            organizationId,
            tenantId,
            config.adapterIdentifier,
            config.sourceIdentifier,
            config.destinationIdentifier,
            operationIdentifier,
            {
                "InstanceUrl": selectedInstance,
            },
            destinationSourceFieldMappings,
            { "X-MS-Access-Token": instanceToken.accessToken }
        ).catch((error) => {
            toast.error("Ooops! Something went wrong while fetching the preview. Please try again.");
        });
    }

    const handleFieldMappingChange = (fieldMapping, value) => {
        const updatedMappings = selectImportConfiguration?.importConfigurationFieldMappings.map((mapping) => {
            if (mapping.id === fieldMapping.id) {
                return { ...mapping, sourceFieldIdentifier: value };
            }
            return mapping;
        });
        setImportConfiguration((prev) => ({
            ...prev,
            importConfigurationFieldMappings: updatedMappings,
        }));
    };

    const handleFieldMappingDelete = (fieldMapping) => {
        const updatedMappings = selectImportConfiguration.importConfigurationFieldMappings.filter(
            (mapping) => mapping.id !== fieldMapping.id
        );
        setImportConfiguration((prev) => ({
            ...prev,
            importConfigurationFieldMappings: updatedMappings,
        }));
    };

    return (
        <Flex style={{ width: "100%" }} justify="center" align="center" vertical gap={20}>
            <FieldsList
                importConfiguration={selectImportConfiguration}
                importConfigurationFieldMappings={selectImportConfiguration?.importConfigurationFieldMappings}
                sourceProperties={sourcePropertyOptions}
                handleFieldMappingDelete={handleFieldMappingDelete}
                handleFieldMappingChange={handleFieldMappingChange}
                sourceFieldsTitle="Source Fields"
            />
            <CustomButton
                type="primary"
                onClick={onSaveConfiguration}
                disabled={isLoading}
                style={{ width: "100%" }}
            >
                Save Configuration
            </CustomButton>
            <CustomButton
                type="secondary"
                onClick={() => handleGetPreview(importConfiguration)}
                disabled={isLoading}
                style={{ width: "100%" }}
            >
                Get Preview
            </CustomButton>
        </Flex>
    );
   
}